import React, { useState } from 'react';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminDataProtection } from '../../hooks/useAdminDataProtection';
import { formatDate, formatTime } from '../../utils/dateUtils';

const AdminDataProtection = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const {
    records,
    isLoading,
    error,
    filters,
    statusOptions,
    complianceStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats,
    getComplianceStatusColor
  } = useAdminDataProtection();

  // Define table columns
  const columns = [
    {
      key: 'id',
      label: 'Record ID',
      sortable: true,
      width: 'w-32',
      render: (record) => (
        <span className="font-mono text-sm font-medium text-primary-600">
          #{record.id.substring(0, 8)}
        </span>
      )
    },
    {
      key: 'record_type',
      label: 'Type',
      sortable: true,
      width: 'w-40',
      render: (record) => (
        <span className="text-sm font-medium text-gray-900 capitalize">
          {record.record_type?.replace('_', ' ') || 'N/A'}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (record) => (
        <p className="text-sm font-medium text-gray-900 truncate">
          {record.title || 'N/A'}
        </p>
      )
    },
    {
      key: 'data_category',
      label: 'Data Category',
      sortable: true,
      width: 'w-40',
      render: (record) => (
        <span className="text-sm text-gray-900 capitalize">
          {record.data_category || 'N/A'}
        </span>
      )
    },
    {
      key: 'compliance_status',
      label: 'Compliance',
      sortable: true,
      width: 'w-40',
      render: (record) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getComplianceStatusColor(record.compliance_status)}`}>
          {record.compliance_status ? record.compliance_status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
        </span>
      )
    },
    {
      key: 'next_review',
      label: 'Next Review',
      sortable: true,
      width: 'w-32',
      render: (record) => (
        record.next_review ? (
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900">
                {formatDate(record.next_review, 'short')}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        )
      )
    },
    {
      key: 'assigned_to',
      label: 'Assigned To',
      sortable: true,
      width: 'w-48',
      render: (record) => (
        record.assigned_to ? (
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {record.assignedTo?.name || 'N/A'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {record.assignedTo?.email || ''}
            </p>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Unassigned</span>
        )
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (record) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(record.created_at, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {formatTime(record.created_at)}
            </div>
          </div>
        </div>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View',
      icon: Eye,
      variant: 'default',
      onClick: (record) => {
        console.log('View record:', record);
      }
    },
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (record) => {
        setEditingRecord(record);
        setShowCreateModal(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (record) => {
        if (window.confirm('Are you sure you want to delete this record?')) {
          // deleteRecord(record.id);
        }
      }
    }
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data Protection Records</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load data protection records. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-6 h-6" />
                Data Protection
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage data protection records and compliance tracking
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingRecord(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Record
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_records || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Compliant</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.compliant || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Non-Compliant</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.non_compliant || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Under Review</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.under_review || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <SearchSelect
              label="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={statusOptions}
            />
            <SearchSelect
              label="Compliance Status"
              value={filters.compliance_status}
              onChange={(value) => handleFilterChange('compliance_status', value)}
              options={complianceStatusOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={records}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search by title, description, type, or category..."
            onSearch={handleSearch}
            emptyMessage="No data protection records found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDataProtection;


